import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericTable from '../../../common/GenericTable'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import LoadingSpinner from '../../../common/LoadingSpinner'
import Dropdown from '../../../common/Dropdown'
import SimpleButton from '../../../common/SimpleButton'
import ActionsMenu from '../../../common/ActionsMenu'
import { toast } from '../../../common/Toast'
import { DownloadIcon, FilterTableIcon, InformationIcon, MenuKebabVerticalBackgroundIcon, MenuKebabVerticalIcon } from '../../../../icons'
import { transformChartData, getLastNYearsRangeISO, sortModelsAlphabetically, downloadFileUTF8 } from '../../../../utils/functions'
import countries from '../../../../utils/countries.json'
import { convertTimeWithTAndZ } from '../../../../utils/filters/date'
import { useWindowSize } from '../../../../libs/hooks'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import { loadModels, loadRegions } from '../../../../actions/shared'
import {
  clearReportsMessages,
  downloadRegisteredDevicesData,
  loadRegisteredDevicesChartData,
  loadRegisteredDevicesCountries,
  loadRegisteredDevicesTableData,
  refreshRegisteredDevicesData,
} from '../../../../actions/reports'
import RegisteredDevicesChart from './RegisteredDevicesChart'
import ViewDeviceModal from './ViewDeviceModal'
import '../../internal-reports.scss'

const orderingFields = [
  {
    field: 'serialNumber',
    order: 'asc',
    translationKey: 'serial_number_asc',
  },
  {
    field: 'serialNumber',
    order: 'desc',
    translationKey: 'serial_number_desc',
  },
  {
    field: 'model',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'model',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'onlineAccessStatus',
    order: 'asc',
    translationKey: 'online_access_asc',
  },
  {
    field: 'onlineAccessStatus',
    order: 'desc',
    translationKey: 'online_access_desc',
  },
  {
    field: 'name',
    order: 'asc',
    translationKey: 'name_asc',
  },
  {
    field: 'name',
    order: 'desc',
    translationKey: 'name_desc',
  },
  {
    field: 'deviceRegistrationDate',
    order: 'asc',
    translationKey: 'device_registration_date_asc',
  },
  {
    field: 'deviceRegistrationDate',
    order: 'desc',
    translationKey: 'device_registration_date_desc',
  },
  {
    field: 'country',
    order: 'asc',
    translationKey: 'country_asc',
  },
  {
    field: 'country',
    order: 'desc',
    translationKey: 'country_desc',
  },
  {
    field: 'cycleCount',
    order: 'asc',
    translationKey: 'cycle_count_asc',
  },
  {
    field: 'cycleCount',
    order: 'desc',
    translationKey: 'cycle_count_desc',
  },
]

const { startISO: defaultStartISO, endISO: defaultEndISO, timeZone } = getLastNYearsRangeISO(3)

const RegisteredDevicesReports = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [registeredDevicesTableData, setRegisteredDevicesTableData] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  const [selectedFilterOnlineAccess, setSelectedFilterOnlineAccess] = useState([])
  const [selectedFilterCountry, setSelectedFilterCountry] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState([])
  const [selectedFilterRegion, setSelectedFilterRegion] = useState([])
  const [startDateFilter, setStartDateFilter] = useState(defaultStartISO)
  const [endDateFilter, setEndDateFilter] = useState(defaultEndISO)
  const [searchParam, setSearchParam] = useState(null)
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showChartFiltersModal, setShowChartFiltersModal] = useState(false)
  const [chartFrequency, setChartFrequency] = useState('year')
  const [chartType, setChartType] = useState('bar')
  const [expandedDevice, setExpandedDevice] = useState(0)
  const chartData = transformChartData(props.registeredDevicesChart)
  const onlineAccessStatuses = [t('reports.associated'), t('reports.unassociated')]
  const chartTypeKeys = ['line', 'bar', 'stackedBar', 'pie']
  const chartFrequencyKeys = ['month', 'quarter', 'year']
  const getCountryName = (code) => countries.find((c) => c.code === code)?.name || code

  const isChartDataEmpty = (cd, freq) => {
    if (!cd) return true
    if (Array.isArray(cd)) return cd.length === 0
    if (freq && Array.isArray(cd[freq])) return cd[freq].length === 0
    const keys = ['month', 'quarter', 'year'].filter((k) => Array.isArray(cd[k]))
    return keys.length ? keys.every((k) => cd[k].length === 0) : true
  }
  const hasChart = !isChartDataEmpty(chartData, chartFrequency)
  const hasTable = registeredDevicesTableData.length > 0

  const chartFilterOptions = [
    {
      name: t('reports.frequency'),
      type: FilterType.singleSelect,
      dataset: chartFrequencyKeys,
      input: chartFrequency,
      output: (selected) => setChartFrequency(selected),
      required: true,
    },
    {
      name: t('reports.chart_type'),
      type: FilterType.singleSelect,
      dataset: chartTypeKeys,
      input: chartType,
      output: (selected) => setChartType(selected),
      required: true,
    },
  ]

  // useEffect(() => {
  //   if (selectedFilterCountry.length === 0 && selectedFilterRegion.length > 0) {
  //     setSelectedFilterRegion([])
  //   }
  // }, [selectedFilterCountry, selectedFilterRegion.length])

  useEffect(() => {
    if (props.deviceModels.length === 0) {
      props.actions.loadModels()
    }

    if (props.registeredDevicesCountries.length === 0) {
      props.actions.loadRegisteredDevicesCountries()
    }

    if (props.reportsRegions.length === 0) {
      props.actions.loadRegions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return
    const t = window.setTimeout(
      () => {
        setSelectedPage(1)
        handleLoadTableData(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => clearTimeout(t)
  }, [
    searchParam,
    selectedFilterOnlineAccess,
    selectedFilterCountry,
    selectedFilterModel,
    selectedFilterRegion,
    startDateFilter,
    endDateFilter,
    orderBy,
    shouldApplyFilters,
  ])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return
    handleLoadChartData()
  }, [selectedFilterOnlineAccess, startDateFilter, endDateFilter, selectedFilterCountry, selectedFilterModel, selectedFilterRegion, shouldApplyFilters])

  useEffect(() => {
    const tableData = props.registeredDevicesTable.map((device, index) => ({
      index,
      serialNumber: device.serialNumber,
      model: device.model,
      deviceRegistrationDate: convertTimeWithTAndZ(device.deviceRegistrationDate).split(' ')[0],
      onlineAccessStatus: device.onlineAccessStatus ? t('reports.associated') : t('reports.unassociated'),
      onlineAccessActivationDate: device.onlineAccessActivationDate,
      cycleCount: device.cycleCount,
      name: device.name,
      email: device.email,
      country: getCountryName(device.country) || t('unknown'),
      address: device.address,
      city: device.city,
      state: device.state,
      postCode: device.postCode,
      phoneNumber: device.phoneNumber,
      actions: [
        {
          label: 'view_details',
          onActionClick: (e) => {
            e.stopPropagation()
            handleOpenViewModal(device.serialNumber)
          },
        },
      ],
    }))

    setRegisteredDevicesTableData(tableData)
  }, [props.registeredDevicesTable])

  const getUniqueRegions = (regions) => {
    if (!regions || regions.length === 0) {
      return []
    }

    const names = regions.map((region) => region.name)
    const uniqueNames = [...new Set(names)]
    return uniqueNames.sort((a, b) => a.localeCompare(b))
  }

  const handleOpenViewModal = (serialNumber) => {
    const device = props.registeredDevicesTable.find((device) => device.serialNumber === serialNumber)

    if (device) {
      setShowViewModal(true)
      setSelectedDevice(device)
    }
  }

  const closeModal = () => {
    setShowViewModal(false)
    setSelectedDevice(null)
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterOnlineAccess.length === 1) {
      let value = selectedFilterOnlineAccess[0] === t('reports.associated') ? true : false
      queryParams = { ...queryParams, onlineAccessStatus: value }
    }

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (selectedFilterCountry.length > 0) {
      const countryCodes = props.registeredDevicesCountries
        .filter((c) => selectedFilterCountry.includes(c.name))
        .map((c) => c.countryCode)
        .filter(Boolean)

      if (countryCodes.length) {
        queryParams = { ...queryParams, country: countryCodes }
      }
    }

    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (startDateFilter && endDateFilter) {
      queryParams = { ...queryParams, timezone: timeZone }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadRegisteredDevicesTableData(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleLoadChartData = () => {
    let queryParams = {}

    if (selectedFilterOnlineAccess.length === 1) {
      let value = selectedFilterOnlineAccess[0] === t('reports.associated') ? true : false
      queryParams = { ...queryParams, onlineAccessStatus: value }
    }

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (selectedFilterCountry.length > 0) {
      const countryCodes = props.registeredDevicesCountries
        .filter((c) => selectedFilterCountry.includes(c.name))
        .map((c) => c.countryCode)
        .filter(Boolean)

      if (countryCodes.length) {
        queryParams = { ...queryParams, country: countryCodes }
      }
    }

    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (startDateFilter && endDateFilter) {
      queryParams = { ...queryParams, timezone: timeZone }
    }

    props.actions.loadRegisteredDevicesChartData(queryParams)
  }

  const handleResetFilters = () => {
    setSelectedFilterOnlineAccess([])
    setSelectedFilterModel([])
    setSelectedFilterRegion([])
    setSelectedFilterCountry([])
    setStartDateFilter(null)
    setEndDateFilter(null)
  }

  const handleResetChartFilters = () => {
    setChartFrequency('year')
    setChartType('bar')
  }

  const handleSearch = (value) => {
    setSearchParam(value)
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  // const handleRefresh = () => {
  //   props.actions.refreshRegisteredDevicesData()
  // }

  const handleDownload = () => {
    let queryParams = createQueryParams()

    props.actions.downloadRegisteredDevicesData(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `registered-devices_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderChartSection = () => {
    if (chartData && !props.isLoading) {
      return (
        <div className={`chart-section ${chartType === 'pie' ? 'pie-chart' : ''}`}>
          <div className="chart-filters-row">
            <div className="chart-title">{t('reports.registered_devices_chart')}</div>
            <SimpleButton className="filter-button" onClick={() => setShowChartFiltersModal(true)}>
              <div className="icon-wrapper">
                <FilterTableIcon />
              </div>
              {t('reports.chart_styles')}
            </SimpleButton>
          </div>
          <RegisteredDevicesChart data={chartData?.[chartFrequency] || []} chartType={chartType} />
          {showChartFiltersModal && (
            <div className="chart-filters-modal-container">
              <FiltersModal
                filters={chartFilterOptions}
                name="styles"
                closeFilters={() => setShowChartFiltersModal(false)}
                modalClassName="chart-filters-modal"
                resetFilters={handleResetChartFilters}
                requireAllRequiredFilters
              />
            </div>
          )}
        </div>
      )
    }
  }

  const renderDevicesTable = () => {
    if (!props.isLoading && !isFirstLoad && registeredDevicesTableData.length === 0) {
      return <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
    }

    if (!isMobile) {
      return (
        <GenericTable
          data={registeredDevicesTableData}
          headers={[
            {
              title: t('serial_number'),
              orderKey: 'serialNumber',
            },
            {
              title: t('model'),
              orderKey: 'model',
            },
            {
              title: t('device_context.online_access'),
              orderKey: 'onlineAccessStatus',
            },
            {
              title: t('reports.device_registration_date'),
              orderKey: 'deviceRegistrationDate',
            },
            {
              title: t('name'),
              orderKey: 'name',
            },
            {
              title: t('country'),
              orderKey: 'country',
            },
            {
              title: t('report-form.cycle-count'),
              orderKey: 'cycleCount',
            },
            {
              title: '',
            },
          ]}
          keys={['serialNumber', 'model', 'onlineAccessStatus', 'deviceRegistrationDate', 'name', 'country', 'cycleCount', 'action']}
          keyRenderer={{
            action: (item) => {
              return (
                <div
                  className="table-actions-wrapper"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleShowActionsMenu(item['index'])
                  }}
                >
                  {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                  {showActionsMenu === item['index'] && (
                    <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={props.isLoading} />
                  )}
                </div>
              )
            },
          }}
          activeSort={orderBy}
          onSort={(orderingField) => handleSort(orderingField)}
          onRowClick={(device) => handleOpenViewModal(device.serialNumber)}
          isLoading={props.isLoading}
        />
      )
    }

    if (isMobile) {
      return (
        <>
          {registeredDevicesTableData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({
                  key: field.translationKey,
                  value: t(`sortable_context.${field.translationKey}`),
                }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => {
                  const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                  handleSort(sortOption)
                }}
              />
            </div>
          )}

          {registeredDevicesTableData.map((device, index) => (
            <div key={index} className="card" onClick={() => setExpandedDevice(index)}>
              <div className={'card-item' + (expandedDevice !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('serial_number')}</div>
                <div className={'card-item-body' + (expandedDevice !== index ? ' align-center' : '')}>
                  <div>{device.serialNumber}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={device.actions} isLoading={props.isLoading} />
                    )}
                  </div>
                </div>
              </div>
              {expandedDevice === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('model')}</div>
                    <div className="card-item-body">{device.model}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('device_context.online_access')}</div>
                    <div className="card-item-body">{device.onlineAccessStatus}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('reports.device_registration_date')}</div>
                    <div className="card-item-body">{device.deviceRegistrationDate}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('name')}</div>
                    <div className="card-item-body">{device.name}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('country')}</div>
                    <div className="card-item-body">{getCountryName(device.country) || t('unknown')}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('report-form.cycle-count')}</div>
                    <div className="card-item-body">{device.cycleCount}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        </>
      )
    }
  }

  const renderTableSection = () => (
    <div className="table-section">
      <div className="actions-wrapper">
        <SearchWithFilter onSearch={handleSearch} />
        <div className="table-actions-row">
          {/* <SimpleButton className="action-button" onClick={() => handleRefresh()} disabled={props.isLoading}>
            <RefreshIcon />
            {t('enhanced_event_log.refresh')}
          </SimpleButton> */}
          <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isLoading || !hasTable}>
            <DownloadIcon />
            {t('download')}
          </SimpleButton>
        </div>
      </div>
      {renderDevicesTable()}
      {canLoadMore && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
    </div>
  )

  return (
    <div className="internal-reports-section registered-devices">
      <div className="actions-wrapper page-section">
        <SearchWithFilter
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={
            (startDateFilter && endDateFilter) ||
            selectedFilterModel.length > 0 ||
            selectedFilterRegion.length > 0 ||
            selectedFilterCountry.length > 0 ||
            selectedFilterOnlineAccess.length > 0
          }
          counterBadge={
            selectedFilterModel.length +
            selectedFilterRegion.length +
            selectedFilterCountry.length +
            selectedFilterOnlineAccess.length +
            (startDateFilter && endDateFilter ? 1 : 0)
          }
        />
      </div>
      <div className="disclaimer-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="disclaimer-wrapper">
          <div className="title">{t('reports.date_disclaimer')}</div>
        </div>
      </div>
      {!hasChart && !hasTable && !isFirstLoad ? (
        <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
      ) : (
        <>
          {renderChartSection()}
          {renderTableSection()}
        </>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: startDateFilter, endDate: endDateFilter },
              output: (startDate, endDate) => {
                setStartDateFilter(startDate)
                setEndDateFilter(endDate)
              },
              quickLinks: [
                { label: t('1_month'), months: 1 },
                { label: t('3_months'), months: 3 },
                { label: t('6_months'), months: 6 },
                { label: t('12_months'), months: 12 },
              ],
            },
            {
              name: t('filter_context.online_access'),
              type: FilterType.multiSelect,
              dataset: onlineAccessStatuses,
              input: selectedFilterOnlineAccess,
              output: (status) => setSelectedFilterOnlineAccess(status),
            },
            {
              name: t('model'),
              type: FilterType.multiSelect,
              dataset: props.deviceModels,
              input: selectedFilterModel,
              output: (model) => setSelectedFilterModel(model),
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.registeredDevicesCountries.map((c) => c.name),
              input: selectedFilterCountry,
              output: (country) => setSelectedFilterCountry(country),
            },
            {
              name: t('region'),
              type: FilterType.multiSelect,
              dataset: getUniqueRegions(props.reportsRegions),
              input: selectedFilterRegion,
              output: (region) => setSelectedFilterRegion(region),
            },
          ]}
          shouldFilter={(shouldApply) => {
            setShouldApplyFilters(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {props.isLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
      {showViewModal && <ViewDeviceModal selectedDevice={selectedDevice} closeModal={closeModal} />}
    </div>
  )
}

function stateToProps({ reports, shared }) {
  const rawModels = [...new Set(shared?.models?.filter((m) => m.ocp_access)?.map((model) => model.model) || [])]

  return {
    registeredDevicesChart: reports?.registeredDevicesChartData || [],
    registeredDevicesTable: reports?.registeredDevicesTableData || [],
    registeredDevicesCountries: reports?.registeredDevicesCountries || [],
    deviceModels: sortModelsAlphabetically(rawModels),
    reportsRegions: shared?.regions || [],
    isLoading: reports?.isLoading,
    canLoadMore: reports?.canLoadMoreTableData,
    errorMessage: reports?.errorMessage,
    successMessage: reports?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadRegisteredDevicesChartData,
        loadRegisteredDevicesTableData,
        loadRegisteredDevicesCountries,
        loadRegions,
        refreshRegisteredDevicesData,
        downloadRegisteredDevicesData,
        clearReportsMessages,
        loadModels,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(RegisteredDevicesReports)

